import { Card, Row, Col } from "react-bootstrap";
import AppLoader from 'components/general/AppLoader';
import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { ProfileContext } from "pages/Profile";
import { useSelector } from "react-redux"
import { Link } from "react-router-dom";
import StandardModal from 'components/general/defaultModals/StandardModal';

const normalizeText = (value) => String(value || '').trim();

const extractAttemptTitle = (assignment) => {
  const raw = String(assignment?.agendaItemTitle || '').trim();
  if (!raw) return normalizeText(assignment?.agendaEventName || '');
  return raw.split(/\r?\n/)[0].trim();
};

function splitTopicName(topicName) {
  const name = String(topicName || '').trim();
  const match = name.match(/^(.*?)\s*\((.*)\)\s*$/);

  if (!match) {
    return { main: name, details: '' };
  }

  return {
    main: (match[1] || '').trim(),
    details: (match[2] || '').trim(),
  };
}

const normalizePathwayAttempt = (value) => {
  if (!value || typeof value !== 'object') return null;

  const userId = String(value.userId || '').trim();
  const userPathwayId = String(value.userPathwayId || '').trim();
  const projectId = String(value.projectId || '').trim();
  const attemptId = String(value.attemptId || '').trim();

  if (!userPathwayId || !projectId || !attemptId) return null;
  return { userId, userPathwayId, projectId, attemptId };
};

const toDateKey = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

async function findAttemptBySpeechTitle({ assignment, meetingDate, profileId, requestHeaders, url }) {
  const speechTitle = extractAttemptTitle(assignment);
  if (!speechTitle) {
    throw new Error('No speech title is available for this assignment.');
  }

  const pathwaysResponse = await fetch(`${url}/userpathway/${profileId}`, {
    method: 'GET',
    headers: requestHeaders,
  });

  if (!pathwaysResponse.ok) {
    throw new Error('Could not load pathway assignments for this user.');
  }

  const userPathways = await pathwaysResponse.json();
  const pathways = Array.isArray(userPathways) ? userPathways : [];
  if (pathways.length === 0) {
    throw new Error('No pathway data is available for this user.');
  }

  const projectResults = await Promise.all(
    pathways.map(async (userPathway) => {
      const userPathwayId = String(userPathway?._id || '').trim();
      if (!userPathwayId) return [];

      const projectsResponse = await fetch(
        `${url}/user/${profileId}/userPathway/${userPathwayId}/projects`,
        { headers: requestHeaders }
      );

      if (!projectsResponse.ok) {
        return [];
      }

      const projects = await projectsResponse.json();
      return Array.isArray(projects)
        ? projects.map((project) => ({ userPathwayId, project }))
        : [];
    })
  );

  const normalizedSpeechTitle = speechTitle.toLowerCase();
  const matches = [];

  projectResults.flat().forEach(({ userPathwayId, project }) => {
    const projectId = String(project?.projectId || '').trim();
    const attempts = Array.isArray(project?.attempts) ? project.attempts : [];

    attempts.forEach((attempt) => {
      if (normalizeText(attempt?.speechTitle).toLowerCase() !== normalizedSpeechTitle) {
        return;
      }

      matches.push({
        userPathwayId,
        projectId,
        attempt,
      });
    });
  });

  if (matches.length === 0) {
    throw new Error('No matching speech attempt was found for this assignment.');
  }

  if (matches.length === 1) {
    return matches[0].attempt;
  }

  const meetingDateKey = toDateKey(meetingDate);
  if (meetingDateKey) {
    const datedMatches = matches.filter((match) => toDateKey(match.attempt?.scheduledDate) === meetingDateKey);
    if (datedMatches.length === 1) {
      return datedMatches[0].attempt;
    }
  }

  throw new Error('Multiple speech attempts match this assignment title. Save the pathway attempt on the agenda item to disambiguate.');
}

async function resolveAssignmentAttempt({ assignment, meetingDate, profileId, authToken, url }) {
  const authHeaders = authToken
    ? { Authorization: `Bearer ${authToken}` }
    : {};

  const requestHeaders = {
    'Content-Type': 'application/json',
    ...authHeaders,
  };

  let pathwayAttempt = normalizePathwayAttempt(assignment?.pathwayAttempt);

  if (!pathwayAttempt && assignment?.agendaItemId) {
    const resolveResponse = await fetch(
      `${url}/agendaItem/${assignment.agendaItemId}/pathway-attempt/resolve`,
      {
        method: 'POST',
        headers: requestHeaders,
      }
    );

    if (resolveResponse.ok) {
      const resolved = await resolveResponse.json();
      pathwayAttempt = normalizePathwayAttempt(resolved);
    }
  }

  if (pathwayAttempt) {
    const targetUserId = String(pathwayAttempt.userId || profileId || '').trim();
    if (!targetUserId) {
      throw new Error('Could not resolve the speech owner for this assignment.');
    }

    const projectResponse = await fetch(
      `${url}/user/${targetUserId}/userPathway/${pathwayAttempt.userPathwayId}/project/${pathwayAttempt.projectId}`,
      { headers: requestHeaders }
    );

    if (!projectResponse.ok) {
      throw new Error('Failed to load pathway project details for this assignment.');
    }

    const project = await projectResponse.json();
    const attempts = Array.isArray(project?.attempts) ? project.attempts : [];
    const matchedAttempt = attempts.find((attempt) => String(attempt?._id || '').trim() === pathwayAttempt.attemptId);
    if (matchedAttempt) {
      return matchedAttempt;
    }

    throw new Error('The speech attempt linked to this assignment was not found in pathway progress.');
  }

  return findAttemptBySpeechTitle({ assignment, meetingDate, profileId, requestHeaders, url });
}

const parseDateValue = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split('/').map(Number);
      const parsed = new Date(year, month - 1, day, 0, 0, 0, 0);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseMeetingDateTime = (meetingDate, meetingTime) => {
  const baseDate = parseDateValue(meetingDate);
  if (!baseDate) return null;

  let hours = 0;
  let minutes = 0;

  if (typeof meetingTime === 'string') {
    const match = meetingTime.trim().match(/^(\d{1,2}):(\d{2})/);
    if (match) {
      hours = Number(match[1]) || 0;
      minutes = Number(match[2]) || 0;
    }
  }

  const parsed = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes, 0, 0);
  return Number.isNaN(parsed.getTime()) ? baseDate : parsed;
};

const formatHumanDate = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return '';

  return parsed.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const statusClassName = (confirmed) => {
  if (confirmed === true) return 'confirmed';
  if (confirmed === false) return 'rejected';
  return 'waiting';
};

const statusLabel = (confirmed) => {
  if (confirmed === true) return 'Confirmed';
  if (confirmed === false) return 'Rejected';
  return 'Awaiting your response';
};

function FeedbacksModal({ show, onHide, assignment, meetingDate, profileId, authToken, url }) {
  const [loading, setLoading] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [error, setError] = useState(null);
  const [speechTitle, setSpeechTitle] = useState('');
  const [attemptDate, setAttemptDate] = useState('');

  useEffect(() => {
    if (!show || !profileId) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setFeedbacks([]);
      setSpeechTitle('');
      setAttemptDate('');
      try {
        const attempt = await resolveAssignmentAttempt({ assignment, meetingDate, profileId, authToken, url });
        setSpeechTitle(normalizeText(attempt?.speechTitle || assignment?.agendaItemTitle || assignment?.agendaEventName || ''));
        setAttemptDate(normalizeText(attempt?.scheduledDate || ''));
        const attemptFeedbacks = Array.isArray(attempt?.feedbacks) ? attempt.feedbacks : [];
        setFeedbacks(attemptFeedbacks);
      } catch (err) {
        setError(err.message || 'Failed to load feedbacks.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [show, assignment, profileId, meetingDate, authToken, url]);

  const title = speechTitle || extractAttemptTitle(assignment) || 'Speech';

  const modalBody = (
    <div style={{ maxHeight: '72vh', overflowY: 'auto' }}>
      {!loading && !error && title && (
        <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card assignments-modal-card mb-3 w-100">
          <Card.Body>
            <h5 className="mb-1">{title}</h5>
            <p className="profile-overview-meta mb-0">{formatHumanDate(attemptDate) || 'No date set'}</p>
          </Card.Body>
        </Card>
      )}
      {loading && <div className="text-center py-3"><AppLoader /></div>}
      {error && <p className="text-danger">{error}</p>}
      {!loading && !error && feedbacks.length === 0 && (
        <p className="mb-0 profile-overview-empty-message">No feedbacks available for this speech.</p>
      )}
      {!loading && feedbacks.length > 0 && (
        <div className="d-grid gap-3">
          {feedbacks.map((fb, idx) => (
            <Card key={fb._id || idx} className="profile-overview-card meetings-mock-card-shell meetings-page-card assignments-modal-card w-100">
              <Card.Body>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {fb.description || fb.text || 'No feedback text provided.'}
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const modalFooter = (
    <button type="button" className="profile-overview-button" onClick={onHide}>Close</button>
  );

  return (
    <StandardModal
      show={show}
      handleClose={onHide}
      title={<span><i className="bi bi-chat-left-text-fill me-2" />Feedbacks - {title}</span>}
      ModalBodyComponent={modalBody}
      ModalFooterComponent={modalFooter}
      style={{ maxWidth: '1400px' }}
    />
  );
}

function EvaluationModal({ show, onHide, assignment, meetingDate, profileId, authToken, url }) {
  const [loading, setLoading] = useState(false);
  const [evaluations, setEvaluations] = useState([]);
  const [error, setError] = useState(null);
  const [speechTitle, setSpeechTitle] = useState('');
  const [attemptDate, setAttemptDate] = useState('');

  useEffect(() => {
    if (!show || !profileId) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setEvaluations([]);
      setSpeechTitle('');
      setAttemptDate('');
      try {
        const attempt = await resolveAssignmentAttempt({ assignment, meetingDate, profileId, authToken, url });
        setSpeechTitle(normalizeText(attempt?.speechTitle || assignment?.agendaItemTitle || assignment?.agendaEventName || ''));
        setAttemptDate(normalizeText(attempt?.scheduledDate || ''));
        const attemptEvaluations = Array.isArray(attempt?.evaluations) ? attempt.evaluations : [];
        setEvaluations(attemptEvaluations);
      } catch (err) {
        setError(err.message || 'Failed to load evaluations.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [show, assignment, profileId, meetingDate, authToken, url]);

  const title = speechTitle || extractAttemptTitle(assignment) || 'Speech';

  const modalBody = (
    <div style={{ maxHeight: '72vh', overflowY: 'auto' }}>
      {!loading && !error && title && (
        <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card assignments-modal-card mb-3 w-100">
          <Card.Body>
            <h5 className="mb-1">{title}</h5>
            <p className="profile-overview-meta mb-0">{formatHumanDate(attemptDate) || 'No date set'}</p>
          </Card.Body>
        </Card>
      )}
      {loading && <div className="text-center py-3"><AppLoader /></div>}
      {error && <p className="text-danger">{error}</p>}
      {!loading && !error && evaluations.length === 0 && (
        <p className="mb-0 profile-overview-empty-message">No evaluations available for this speech.</p>
      )}
      {!loading && evaluations.length > 0 && (
        <div className="d-grid gap-3">
          {evaluations.map((evaluation, index) => {
            const topics = Array.isArray(evaluation?.topics) ? evaluation.topics : [];
            return (
              <Card key={evaluation?._id || index} className="profile-overview-card meetings-mock-card-shell meetings-page-card assignments-modal-card w-100">
                <Card.Body>
                  <p className="mb-1 fw-semibold">{evaluation?.evaluatorName || 'Unknown evaluator'}</p>
                  {evaluation?.feedback && (
                    <div className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{evaluation.feedback}</div>
                  )}
                  {topics.length > 0 ? (
                    <div className="d-grid gap-2">
                      {topics.map((topic, topicIndex) => {
                        const parsed = splitTopicName(topic?.name);
                        const stars = Math.max(0, Math.min(5, Number(topic?.stars || 0)));
                        return (
                          <div key={`${evaluation?._id || index}-topic-${topicIndex}`} className="border rounded p-2">
                            <div className="fw-bold">{parsed.main || topic?.name || 'Topic'}</div>
                            {parsed.details && (
                              <div className="small" style={{ opacity: 0.85 }}>
                                ({parsed.details})
                              </div>
                            )}
                            <div className="mt-1 mb-1">
                              {[1, 2, 3, 4, 5].map((starValue) => (
                                <i
                                  key={`${evaluation?._id || index}-topic-${topicIndex}-star-${starValue}`}
                                  className={stars >= starValue ? 'bi bi-star-fill' : 'bi bi-star'}
                                  style={{ color: stars >= starValue ? '#ffc107' : 'var(--foreground)', marginRight: 3 }}
                                ></i>
                              ))}
                            </div>
                            {topic?.shortComment && (
                              <div style={{ whiteSpace: 'pre-wrap' }}>{topic.shortComment}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mb-0">No topic ratings available</p>
                  )}
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  const modalFooter = (
    <button type="button" className="profile-overview-button" onClick={onHide}>Close</button>
  );

  return (
    <StandardModal
      show={show}
      handleClose={onHide}
      title={<span><i className="bi bi-star-fill me-2" />Evaluation - {title}</span>}
      ModalBodyComponent={modalBody}
      ModalFooterComponent={modalFooter}
      style={{ maxWidth: '1400px' }}
    />
  );
}

export default function Roles() {
  const [feedbacksModal, setFeedbacksModal] = useState({ show: false, assignment: null, meetingDate: null });
  const [evaluationModal, setEvaluationModal] = useState({ show: false, assignment: null, meetingDate: null });
  const { profile } = useContext(ProfileContext)
  const { user: authUser } = useSelector(x => x.auth)
  const url = process.env.REACT_APP_URL;
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingAssignment, setLoadingAssignment] = useState({}); // assignmentId: bool

  const getRoles = useCallback(async () => {
    try {
      const response = await fetch(`${url}/roleAssignmentsByUser/${profile?._id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        }
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setMeetings(Array.isArray(data) ? data : []); // data is an array of { meeting, assignments }
    }
    catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }
  }, [authUser?.token, profile?._id, url]);

  useEffect(() => {
    if (profile && profile._id) {
      getRoles();
    }
  }, [getRoles, profile]);

  const patchAssignment = async (assignmentId, value) => {
    setLoadingAssignment(prev => ({ ...prev, [assignmentId]: true }));
    try {
      const response = await fetch(`${url}/roleAssignment/${assignmentId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(authUser?.token ? { Authorization: `Bearer ${authUser.token}` } : {}),
        },
        body: JSON.stringify({ confirmed: value }),
      });

      if (!response.ok) {
        throw new Error('Failed to update assignment status.');
      }

      setMeetings((prev) => prev.map((entry) => ({
        ...entry,
        assignments: Array.isArray(entry.assignments)
          ? entry.assignments.map((assignment) => (
            String(assignment?._id || '') === String(assignmentId)
              ? { ...assignment, confirmed: value }
              : assignment
          ))
          : entry.assignments,
      })));
    } catch (error) {
      console.error('Failed to patch assignment:', error);
    } finally {
      setLoadingAssignment(prev => ({ ...prev, [assignmentId]: false }));
    }
  };

  const canManageAssignments = Boolean(
    authUser?.user && (
      String(authUser.user._id || authUser.user.id || '') === String(profile?._id || '')
      || (Array.isArray(authUser.user.clubRoles)
        && (authUser.user.clubRoles.includes('President') || authUser.user.clubRoles.includes('Vice President of Education')))
    )
  );

  const assignmentHistory = useMemo(() => {
    const items = [];

    meetings.forEach(({ meeting, assignments }) => {
      const safeAssignments = Array.isArray(assignments) ? assignments : [];
      safeAssignments.forEach((assignment) => {
        items.push({
          assignmentId: String(assignment?._id || ''),
          roleName: normalizeText(assignment?.role?.name || assignment?.agendaEventName || 'N/A'),
          agendaItemTitle: normalizeText(assignment?.agendaItemTitle || ''),
          eventType: normalizeText(assignment?.eventType || ''),
          confirmed: assignment?.confirmed,
          meetingId: String(meeting?._id || ''),
          meetingNumber: normalizeText(meeting?.meetingNumber || ''),
          meetingTheme: normalizeText(meeting?.meetingTheme || 'Untitled meeting'),
          meetingDate: normalizeText(meeting?.meetingDate || ''),
          meetingTime: normalizeText(meeting?.meetingTime || ''),
          meetingDateTime: parseMeetingDateTime(meeting?.meetingDate, meeting?.meetingTime),
          assignment,
          meeting,
        });
      });
    });

    return items.sort((a, b) => {
      const aTs = a.meetingDateTime ? a.meetingDateTime.getTime() : 0;
      const bTs = b.meetingDateTime ? b.meetingDateTime.getTime() : 0;
      return bTs - aTs;
    });
  }, [meetings]);

  const filteredHistory = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return assignmentHistory;

    return assignmentHistory.filter((entry) => {
      const haystack = [
        entry.roleName,
        entry.agendaItemTitle,
        entry.meetingTheme,
        entry.meetingDate,
        statusLabel(entry.confirmed),
      ].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [assignmentHistory, searchTerm]);

  const summary = useMemo(() => {
    const total = assignmentHistory.length;
    const confirmed = assignmentHistory.filter((entry) => entry.confirmed === true).length;
    const rejected = assignmentHistory.filter((entry) => entry.confirmed === false).length;
    const waiting = assignmentHistory.filter((entry) => entry.confirmed !== true && entry.confirmed !== false).length;
    return { total, confirmed, rejected, waiting };
  }, [assignmentHistory]);

  const roleFrequency = useMemo(() => {
    const counter = new Map();
    assignmentHistory.forEach((entry) => {
      const key = entry.roleName || 'N/A';
      counter.set(key, (counter.get(key) || 0) + 1);
    });

    return Array.from(counter.entries())
      .map(([roleName, count]) => ({ roleName, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [assignmentHistory]);

  const maxRoleFrequency = roleFrequency.length > 0
    ? Math.max(...roleFrequency.map((entry) => entry.count))
    : 1;

  return (
    <div className="assignments-history">
      <Row className="g-3 mt-2">
        <Col xs={12} lg={6}>
          <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
            <Card.Body>
              <h4>Assignments Snapshot</h4>
              <div className="profile-overview-divider" />
              <div className="assignments-kpi-grid">
                <div className="assignments-kpi-item">
                  <span className="assignments-kpi-label">Total</span>
                  <strong className="assignments-kpi-value">{summary.total}</strong>
                </div>
                <div className="assignments-kpi-item">
                  <span className="assignments-kpi-label">Confirmed</span>
                  <strong className="assignments-kpi-value assignments-kpi-value--confirmed">{summary.confirmed}</strong>
                </div>
                <div className="assignments-kpi-item">
                  <span className="assignments-kpi-label">Rejected</span>
                  <strong className="assignments-kpi-value assignments-kpi-value--rejected">{summary.rejected}</strong>
                </div>
                <div className="assignments-kpi-item">
                  <span className="assignments-kpi-label">Awaiting</span>
                  <strong className="assignments-kpi-value assignments-kpi-value--waiting">{summary.waiting}</strong>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
            <Card.Body>
              <h4>Role Frequency</h4>
              <div className="profile-overview-divider" />
              {roleFrequency.length === 0 ? (
                <p className="mb-0 profile-overview-empty-message">No role history available yet.</p>
              ) : (
                <div className="assignments-role-bars">
                  {roleFrequency.map((entry) => {
                    const width = Math.max(8, Math.round((entry.count / maxRoleFrequency) * 100));
                    return (
                      <div key={`role-frequency-${entry.roleName}`} className="assignments-role-bar-row">
                        <span className="assignments-role-bar-label">{entry.roleName}</span>
                        <div className="assignments-role-bar-track">
                          <div className="assignments-role-bar-fill" style={{ width: `${width}%` }} />
                        </div>
                        <span className="assignments-role-bar-count">{entry.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col xs={12}>
          <Card className="profile-overview-card meetings-mock-card-shell meetings-page-card">
            <Card.Body>
              <div className="assignments-history-top">
                <h4>Assignments History</h4>
                <span className="profile-overview-meta">{filteredHistory.length} entr{filteredHistory.length === 1 ? 'y' : 'ies'}</span>
              </div>
              <div className="profile-overview-divider" />

              <div className="assignments-search-wrap mb-3">
                <input
                  type="text"
                  className="assignments-search-input"
                  placeholder="Search role, meeting, status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="assignments-search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    <i className="bi bi-x" />
                  </button>
                )}
              </div>

              {filteredHistory.length === 0 ? (
                <p className="mb-0 profile-overview-empty-message">No role assignments found for your filter.</p>
              ) : (
                <div className="assignments-history-table-wrap">
                  <div className="assignments-history-table assignments-history-table--head">
                    <div>Meeting</div>
                    <div>Date</div>
                    <div>Role</div>
                    <div>Status</div>
                    <div>Actions</div>
                  </div>

                  {filteredHistory.map((entry) => {
                    const statusClass = statusClassName(entry.confirmed);
                    const statusText = statusLabel(entry.confirmed);
                    const isBusy = Boolean(loadingAssignment[entry.assignmentId]);
                    const isPathway = entry.eventType.toLowerCase() === 'pathway';
                    const attemptTitle = normalizeText(entry.agendaItemTitle).split(/\r?\n/)[0] || '';
                    const roleDisplayName = isPathway && attemptTitle
                      ? `${entry.roleName || 'N/A'} - ${attemptTitle}`
                      : (entry.roleName || 'N/A');

                    return (
                      <div key={`assignment-row-${entry.assignmentId}-${entry.meetingId}`} className="assignments-history-table assignments-history-table--row">
                        <div className="assignments-history-cell" data-label="Meeting">
                          {entry.meetingId ? (
                            <Link to={`/meeting/${entry.meetingId}`} className="profile-overview-card-link">
                              #{entry.meetingNumber || 'N/A'} {entry.meetingTheme || 'Untitled'}
                            </Link>
                          ) : (
                            <span>{entry.meetingTheme || 'Untitled'}</span>
                          )}
                        </div>

                        <div className="assignments-history-cell" data-label="Date">
                          <span>{entry.meetingDate || 'TBA'}</span>
                        </div>

                        <div className="assignments-history-cell" data-label="Role">
                          <span>{roleDisplayName}</span>
                        </div>

                        <div className="assignments-history-cell" data-label="Status">
                          <span className={`assignments-history-status assignments-history-status--${statusClass}`}>
                            {statusText}
                          </span>
                        </div>

                        <div className="assignments-history-cell assignments-history-actions" data-label="Actions">
                          {isPathway && (
                            <>
                              <button
                                type="button"
                                className="assignments-history-btn assignments-history-btn--info"
                                onClick={() => setFeedbacksModal({ show: true, assignment: entry.assignment, meetingDate: entry.meetingDate })}
                              >
                                Feedbacks
                              </button>
                              <button
                                type="button"
                                className="assignments-history-btn assignments-history-btn--neutral"
                                onClick={() => {
                                  setEvaluationModal({ show: true, assignment: entry.assignment, meetingDate: entry.meetingDate });
                                }}
                              >
                                Evaluation
                              </button>
                            </>
                          )}

                          {canManageAssignments && (
                            entry.confirmed === undefined || entry.confirmed === null
                              ? (
                                <>
                                  <button
                                    type="button"
                                    className="assignments-history-btn assignments-history-btn--danger"
                                    disabled={isBusy}
                                    onClick={() => patchAssignment(entry.assignmentId, false)}
                                  >
                                    Decline
                                  </button>
                                  <button
                                    type="button"
                                    className="assignments-history-btn assignments-history-btn--success"
                                    disabled={isBusy}
                                    onClick={() => patchAssignment(entry.assignmentId, true)}
                                  >
                                    Accept
                                  </button>
                                </>
                              )
                              : (
                                <button
                                  type="button"
                                  className="assignments-history-btn assignments-history-btn--neutral"
                                  disabled={isBusy}
                                  onClick={() => patchAssignment(entry.assignmentId, null)}
                                >
                                  Reset
                                </button>
                              )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Feedbacks modal */}
      <FeedbacksModal
        show={feedbacksModal.show}
        onHide={() => setFeedbacksModal({ show: false, assignment: null, meetingDate: null })}
        assignment={feedbacksModal.assignment}
        meetingDate={feedbacksModal.meetingDate}
        profileId={profile._id}
        authToken={authUser?.token}
        url={url}
      />
      <EvaluationModal
        show={evaluationModal.show}
        onHide={() => setEvaluationModal({ show: false, assignment: null, meetingDate: null })}
        assignment={evaluationModal.assignment}
        meetingDate={evaluationModal.meetingDate}
        profileId={profile._id}
        authToken={authUser?.token}
        url={url}
      />
    </div>
  );
}